import React from "react";
import { Skeleton } from "@material-ui/lab";
import { Autocomplete, DatePicker, Field, Text, TextField } from "components/commons";
import styles from "./purchase-order.module.scss";
import { useFormContext } from "contexts/form-context";
import moment from "moment";
import TimePickerV2 from "components/commons/time-picker/time-picker-v2";
import { purchaseOrderRecipient } from "data/constant/purchase-order";
import locale from "localization";
import { FormMode } from "enums";

const DisplayText = ({ value, formatter, width = "300px", loading }) => {
  if (loading) return <Skeleton width={width} height="30px" />;

  if (React.isValidElement(value)) {
    return value;
  }

  return <>{formatter ? formatter(value) : value}</>;
};

const ViewRow = ({ label, value, formatter, loading }) => (
  <div className={styles.viewRow}>
    <Text strong color="black">
      {label}
    </Text>
    <Text color="black">
      <DisplayText value={value} formatter={formatter} loading={loading} />
    </Text>
  </div>
);

const PurchaseOrderInformationForm = ({ purchaseOrder, formMode }) => {
  const { fields, modifyField, modifyForm } = useFormContext();

  const handleTypeChange = (name, props) => {
    if (props?.value === fields?.productType?.value) return;
    modifyForm({
      [name]: props,
      products: {
        value: [],
      },
    });
  };

  const PRODUCT_TYPE_OPTIONS = [
    {
      value: "fuel",
      label: "Fuel",
    },
  ];

  return (
    <>
      {formMode === FormMode.Edit && (
        <>
          <div className={styles.dateRow}>
            <Field
              {...fields.purchaseOrderNumber}
              labelClassName={styles.fieldLabel}
              childrenClassName={styles.fieldContent}
            >
              <TextField
                {...fields.purchaseOrderNumber}
                readOnly={formMode === FormMode.Edit}
                disabled={formMode === FormMode.Edit}
              />
            </Field>
          </div>
          <div className={styles.dateRow}>
            <Field
              {...fields.salesOrderNumber}
              labelClassName={styles.fieldLabel}
              childrenClassName={styles.fieldContent}
            >
              <TextField
                {...fields.salesOrderNumber}
                readOnly={formMode === FormMode.Edit}
                disabled={formMode === FormMode.Edit}
              />
            </Field>
          </div>
        </>
      )}

      <Field
        {...fields.recipient}
        label={locale.orderRecipient}
        labelClassName={styles.fieldLabel}
        childrenClassName={styles.fieldContent}
      >
        <Autocomplete
          {...fields.recipient}
          onChange={modifyField}
          options={purchaseOrderRecipient.map((val) => ({
            label: val,
            value: val?.toLowerCase(),
          }))}
          Component={Autocomplete}
        />
      </Field>

      <Text color="black" className={styles.sectionTitle}>
        {locale.preferredDeliveryDateTime}
      </Text>

      <div className={styles.dateRow}>
        <Field
          {...fields.date}
          labelClassName={styles.fieldLabel}
          childrenClassName={styles.fieldContent}
        >
          <DatePicker
            {...fields.date}
            prettify={(date) => moment(date).format("YYYY-MM-DD HH:mm")}
            onChange={modifyField}
            Component={DatePicker}
            disableStartDate={new Date()}
          />
        </Field>

        <Field
          {...fields.time}
          labelClassName={styles.fieldLabel}
          childrenClassName={styles.fieldContent}
        >
          <TimePickerV2
            {...fields.time}
            variant="purple"
            onChange={modifyField}
            minTime={
              fields?.date?.value
                ? moment(fields.date.value).isAfter(moment(), "day")
                  ? moment(fields.date.value).startOf("day").toDate()
                  : new Date()
                : new Date()
            }
            date={fields.date.value}
          />
        </Field>
      </div>
      <div className={styles.dateRow}>
        <Field
          label={locale.productType}
          labelClassName={styles.fieldLabel}
          childrenClassName={styles.fieldContent}
        >
          <Autocomplete
            {...fields.productType}
            placeholder={locale.selectProductType}
            options={PRODUCT_TYPE_OPTIONS}
            onChange={handleTypeChange}
          />
        </Field>
      </div>
      <div className={styles.dateRow}>
        <Field {...fields.remarks} label={locale.remarks} labelClassName={styles.fieldLabel}>
          <TextField multiline {...fields?.remarks} onChange={modifyField} />
        </Field>
      </div>
      <ViewRow label={locale.headOfficeRemarks} value={fields?.remarksHo?.value} />
    </>
  );
};

export default PurchaseOrderInformationForm;
